import { useState } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';

import { CardSelect } from '@common/ui/CardSelect';
import { AutoComplete } from '@common/ui/inputs/AutoComplete';
import { Input } from '@common/ui/inputs/Input';
import { InputDate } from '@common/ui/inputs/InputDate';
import { InputSelect } from '@common/ui/inputs/InputSelect';
import { Heading } from '@common/ui/typography/Heading';

import { cities } from '@entities/profile/constants/cities';
import { gendersDict, goalsDict } from '@entities/profile/dicts';

import type { SignUpFormData } from '../../SignUp';

interface BasicInfoProps {
  control: Control<SignUpFormData>;
  errors: FieldErrors<SignUpFormData>;
  /** The light (matchmaker) sign-up has no goal. */
  isGoalShown?: boolean;
}

export const BasicInfo = ({
  control,
  errors,
  isGoalShown = true,
}: BasicInfoProps) => {
  const [isOpenGoalModal, setIsOpenGoalModal] = useState(false);

  return (
    <>
      <Heading level={2} style={{ textAlign: 'center' }}>
        Основное о вас
      </Heading>

      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <Input
            data-test-id="basic-info-name-input"
            required
            placeholder="Имя"
            description="Используйте настоящее, потом изменить его будет нельзя"
            value={field.value || ''}
            onReset={() => field.onChange('')}
            onChange={(e) => field.onChange(e.target.value)}
            error={errors.name?.message?.toString()}
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        control={control}
        name="dateOfBirth"
        render={({ field }) => (
          <InputDate
            data-test-id="basic-info-date-of-birth-input"
            required
            placeholder="Дата рождения"
            description="Используйте настоящую, потом изменить её будет нельзя"
            value={field.value}
            onChange={(date) => field.onChange(date)}
            error={errors.dateOfBirth?.message?.toString()}
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        control={control}
        name="city"
        render={({ field }) => (
          <AutoComplete
            required
            items={cities.map((city) => ({
              value: city,
              title: city,
            }))}
            placeholder="Город"
            searchPlaceholder="Поиск города"
            value={field.value || ''}
            onSelect={(value) => field.onChange(value)}
            error={errors.city?.message?.toString()}
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        control={control}
        name="gender"
        render={({ field }) => (
          <CardSelect
            value={field.value || ''}
            items={gendersDict}
            onChange={(item) => field.onChange(item.value)}
            onBlur={field.onBlur}
          />
        )}
      />

      {isGoalShown && (
        <Controller
          control={control}
          name="goal"
          render={({ field }) => (
            <InputSelect
              isOpenModal={isOpenGoalModal}
              setIsOpenModal={setIsOpenGoalModal}
              required
              items={goalsDict}
              value={field.value || ''}
              onSelect={(value) => field.onChange(value)}
              placeholder="Я ищу"
              gap={0}
              error={errors.goal?.message?.toString()}
              onBlur={field.onBlur}
            />
          )}
        />
      )}
    </>
  );
};
